import type {
  FetchProductListParams,
  Product,
  ProductDeleteParams
} from 'Services/ProductManagement/Features/Product/types'
import type { ListResponse } from 'Services/ProductManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

export enum TagTypes {
  PRODUCT = 'Product'
}

export const productApi = createBaseApi({
  reducerPath: 'productApi',
  keepUnusedDataFor: 5,
  tagTypes: [TagTypes.PRODUCT]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getProducts: builder.query<ListResponse<Product>, FetchProductListParams | void>({
      query: params => ({
        url: '/api/v1/product/',
        params: params ?? undefined
      }),
      providesTags: result => {
        if (result) {
          return [
            ...result.resources.map(({ id }) => ({ type: TagTypes.PRODUCT, id })),
            { type: TagTypes.PRODUCT, id: RTKCacheTags.LIST }
          ]
        }
        return [{ type: TagTypes.PRODUCT, id: RTKCacheTags.LIST }]
      }
    }),
    getSingleProduct: builder.query<Product, string>({
      query: id => `/api/v1/product/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.PRODUCT, id }]
    }),
    createSingleProduct: builder.mutation<Product, Partial<Product>>({
      query: data => ({
        url: '/api/v1/product/',
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: TagTypes.PRODUCT, id: RTKCacheTags.LIST },
        { type: TagTypes.PRODUCT, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    updateSingleProduct: builder.mutation<Product, { id: string } & Partial<Product>>({
      query: data => {
        const { id, ...fields } = data
        return {
          url: `/api/v1/product/${id}/`,
          method: 'PUT',
          body: fields
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: TagTypes.PRODUCT, id },
        { type: TagTypes.PRODUCT, id: RTKCacheTags.LIST },
        { type: TagTypes.PRODUCT, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    deleteSingleProduct: builder.mutation<
      Record<string, never>,
      { id: string } & ProductDeleteParams
    >({
      query: ({ id, ...params }) => ({
        url: `/api/v1/product/${id}/`,
        method: 'DELETE',
        params
      }),
      invalidatesTags: [
        { type: TagTypes.PRODUCT, id: RTKCacheTags.LIST },
        { type: TagTypes.PRODUCT, id: RTKCacheTags.LIST_ALL }
      ]
    })
  })
})

export const {
  useGetProductsQuery,
  useGetSingleProductQuery,
  useCreateSingleProductMutation,
  useUpdateSingleProductMutation,
  useDeleteSingleProductMutation
} = productApi
